/**
 * VoBizDevice — drop-in replacement for Twilio Device when using India (VoBiz) calling.
 *
 * Wraps SIP.js to expose the same surface as Twilio Voice SDK so DialerSliderCard.js
 * needs minimal changes: same device.connect(), call.on(), call.disconnect(), call.mute(), etc.
 *
 * Requires: npm install sip.js
 */

import {
  UserAgent,
  Registerer,
  Inviter,
  SessionState,
  RegistererState
} from "sip.js";
import { makeApi, URLS } from "Utils/apiURL";

// ---------------------------------------------------------------------------
// VoBizCallSession — wraps an SIP.js Inviter to look like a Twilio Call object
// ---------------------------------------------------------------------------
class VoBizCallSession {
  constructor(inviter) {
    this.inviter = inviter;
    this._listeners = {};
    this._callStatus = "connecting"; // mirrors Twilio: open / closed / ringing / connecting
    this.callSid = null; // set externally after backend logs the call

    inviter.stateChange.addListener(state => this._onStateChange(state));
  }

  _onStateChange(state) {
    console.log("[VoBiz] session state:", state, "| listeners:",
      Object.keys(this._listeners).map(k => `${k}(${this._listeners[k].length})`).join(","));
    switch (state) {
      case SessionState.Establishing:
        this._callStatus = "ringing";
        // VoBiz keeps the A-leg UNANSWERED while it dials (SIP stays in Proceeding; the 200 OK
        // arrives only when the callee picks up), and its India route delivers no ringback over
        // the early-media channel — so the whole ringing phase is dead air. Generate the ring
        // locally, the same way Twilio's SDK plays its "outgoing" sound. Started here (in the
        // Call-click gesture's call stack) so autoplay policy can't block the AudioContext.
        this._startLocalRingback();
        // Start polling for peer connection so we can hook ontrack for early media (183 ringback)
        this._watchForEarlyMedia();
        break;

      case SessionState.Established:
        this._callStatus = "open";
        // 200 OK = the callee actually answered — stop the ring the moment they're on the line.
        this._stopLocalRingback();
        this._attachRemoteAudio();
        this._startMediaWatchdog();
        this._emit("accept", this._makeConnProxy());
        break;

      case SessionState.Terminated: {
        // If the media watchdog already declared this call dead (dead-ws case), don't
        // emit a second disconnect when the SIP layer eventually catches up.
        const alreadyClosed = this._callStatus === "closed";
        this._callStatus = "closed";
        this._stopLocalRingback();
        this._stopMediaWatchdog();
        if (!alreadyClosed) this._emit("disconnect", this._makeConnProxy());
        break;
      }

      default:
        break;
    }
  }

  /** Mimics the Twilio connection object passed to event handlers */
  _makeConnProxy() {
    return {
      status: () => this._callStatus,
      mediaStream: { CallSid: this.callSid },
      parameters: { CallSid: this.callSid },
      mute: (val) => this.mute(val),
      unmute: () => this.unmute(),
      isMuted: () => this.isMuted(),
      disconnect: () => this.disconnect(),
      sendDigits: (digits) => this.sendDigits(digits),
      getStats: async () => {
        try {
          const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
          if (pc) return await pc.getStats();
        } catch (_) {}
        return [];
      }
    };
  }

  // ---- Twilio Call-compatible API ----------------------------------------

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  async disconnect() {
    try {
      if (this.inviter.state === SessionState.Establishing) {
        await this.inviter.cancel();
      } else if (this.inviter.state === SessionState.Established) {
        await this.inviter.bye();
      }
    } catch (e) {
      console.error("[VoBiz] disconnect error:", e);
    }
  }

  /**
   * Supports both mute() and mute(true/false) to match Twilio SDK variants.
   */
  mute(value) {
    const shouldMute = value !== false; // undefined / true → mute; false → unmute
    this._setAudioEnabled(!shouldMute);
  }

  unmute() {
    this._setAudioEnabled(true);
  }

  isMuted() {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        const sender = pc.getSenders().find(s => s.track?.kind === "audio");
        return sender ? !sender.track.enabled : false;
      }
    } catch (_) {}
    return false;
  }

  sendDigits(digits) {
    try {
      // Prefer WebRTC DTMF sender
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        const dtmfSender = pc.getSenders().find(s => s.dtmf);
        if (dtmfSender?.dtmf) {
          dtmfSender.dtmf.insertDTMF(String(digits));
          return;
        }
      }
      // Fallback: SIP INFO method
      this.inviter.info({
        requestOptions: {
          body: {
            contentDisposition: "render",
            contentType: "application/dtmf-relay",
            content: `Signal=${digits}\r\nDuration=100`
          }
        }
      });
    } catch (e) {
      console.error("[VoBiz] sendDigits error:", e);
    }
  }

  /** Returns Twilio-compatible status string */
  status() {
    return this._callStatus;
  }

  // ---- Internal helpers ---------------------------------------------------

  /** Poll for the RTCPeerConnection and attach audio as soon as any remote track arrives (early media / 183) */
  _watchForEarlyMedia() {
    const tryAttach = () => {
      if (this._callStatus === "open" || this._callStatus === "closed") return; // already handled
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        // Hook ontrack so we attach the moment a remote track arrives
        pc.addEventListener("track", () => {
          if (this._callStatus === "ringing") {
            this._attachRemoteAudio();
          }
        });
        // Also try immediately in case tracks are already present
        this._attachRemoteAudio();
      } else {
        // PC not ready yet — retry in 50ms
        setTimeout(tryAttach, 50);
      }
    };
    setTimeout(tryAttach, 50);
  }

  _attachRemoteAudio() {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (!pc) return;
      const remoteStream = new MediaStream();
      pc.getReceivers().forEach(receiver => {
        if (receiver.track) remoteStream.addTrack(receiver.track);
      });
      let audio = document.getElementById("vobiz-remote-audio");
      if (!audio) {
        audio = document.createElement("audio");
        audio.id = "vobiz-remote-audio";
        audio.autoplay = true;
        document.body.appendChild(audio);
      }
      audio.srcObject = remoteStream;
      console.log("[VoBiz] Remote audio attached, tracks:", remoteStream.getTracks().length);
    } catch (e) {
      console.error("[VoBiz] _attachRemoteAudio error:", e);
    }
  }

  /**
   * Media watchdog — the authoritative "is this call still alive" check, active only
   * while Established. The SIP BYE from the far end rides the WebSocket, and a ws that
   * died mid-call (network blip, sleep, roaming) swallows it silently: FS tears the
   * call down, RTP stops instantly, but the browser UI stays "connected" forever.
   * Inbound RTP never pauses on a live call (comfort noise flows even in silence), so
   * 10s of zero inbound packets = the bridge is gone. Same approach as Twilio's SDK
   * media timeout. Production will also get server-pushed call state; this is the
   * client-side belt to that suspenders.
   */
  _startMediaWatchdog() {
    this._stopMediaWatchdog();
    const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
    if (!pc || !pc.getStats) return;
    let lastPackets = -1;
    let stalledMs = 0;
    this._mediaWatchdog = setInterval(async () => {
      if (this._callStatus !== "open") { this._stopMediaWatchdog(); return; }
      try {
        const stats = await pc.getStats();
        let packets = -1;
        stats.forEach(r => {
          if (r.type === "inbound-rtp" && (r.kind === "audio" || r.mediaType === "audio")) {
            packets = r.packetsReceived ?? -1;
          }
        });
        if (packets < 0) return; // stats shape unknown — never false-positive on that
        if (packets > lastPackets) {
          lastPackets = packets;
          stalledMs = 0;
        } else {
          stalledMs += 2000;
        }
        if (stalledMs >= 10000) {
          console.warn("[VoBiz] media watchdog: no inbound audio for 10s — treating as remote hangup");
          this._stopMediaWatchdog();
          try { this.inviter.bye(); } catch (_) {} // best-effort SIP cleanup if transport lives
          this._callStatus = "closed";
          this._emit("disconnect", this._makeConnProxy());
        }
      } catch (_) {
        // getStats hiccup — keep watching.
      }
    }, 2000);
  }

  _stopMediaWatchdog() {
    if (this._mediaWatchdog) {
      clearInterval(this._mediaWatchdog);
      this._mediaWatchdog = null;
    }
  }

  /**
   * Local ringback: 400 Hz double-ring in the Indian cadence (0.4s on, 0.2s off,
   * 0.4s on, 2s off), synthesized with Web Audio — no asset, no network fetch.
   * Runs until the far end produces sustained audio or the call terminates.
   */
  _startLocalRingback() {
    if (this._ringback) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      // The Call click is a user gesture, but resume defensively for strict autoplay policies.
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      // Real ringback is a DUAL tone (400+450 Hz beat) with soft attack/release — a lone
      // square-edged sine reads as "beep beep" (pager), not "ringing" (phone).
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      const oscs = [400, 450].map(f => {
        const o = ctx.createOscillator();
        o.frequency.value = f;
        o.connect(gain);
        o.start();
        return o;
      });
      const osc = { stop: () => oscs.forEach(o => { try { o.stop(); } catch (_) {} }) };
      const RING_GAIN = 0.08; // two summed tones — halve per-tone level
      // One sustained ~1.2s ring, ~3s of quiet: the familiar "trriiing … trriiing" of an
      // Indian mobile ringback, with 20ms ramps so the edges don't click.
      const cadence = () => {
        const t = ctx.currentTime;
        gain.gain.cancelScheduledValues(t);
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(RING_GAIN, t + 0.02);
        gain.gain.setValueAtTime(RING_GAIN, t + 1.15);
        gain.gain.linearRampToValueAtTime(0, t + 1.2);
      };
      // Don't ring at t=0 — a real call has ~2s of quiet while the network dials
      // (INVITE → app → B-leg setup) before the far phone starts ringing. Ringing
      // instantly at click reads as fake. VoBiz gives no "B-leg ringing" signal, so
      // this delay approximates the observed setup time on the India route.
      this._ringback = { ctx, osc, gain, cadenceTimer: null, startTimer: null, vadTimer: null, pollTimer: null };
      this._ringback.startTimer = setTimeout(() => {
        const rb = this._ringback;
        if (!rb) return;
        cadence();
        rb.cadenceTimer = setInterval(cadence, 4200); // 1.2s ring + 3s quiet
      }, 2500);
      this._armRingbackStop();
      this._pollCallStateForRingStop();
    } catch (e) {
      console.warn("[VoBiz] local ringback unavailable:", e);
    }
  }

  _stopLocalRingback() {
    const rb = this._ringback;
    if (!rb) return;
    this._ringback = null;
    try { clearTimeout(rb.startTimer); } catch (_) {}
    try { clearInterval(rb.cadenceTimer); } catch (_) {}
    try { clearInterval(rb.vadTimer); } catch (_) {}
    try { clearInterval(rb.pollTimer); } catch (_) {}
    try { rb.osc.stop(); } catch (_) {}
    try { rb.ctx.close(); } catch (_) {}
  }

  /**
   * Stops the ringback once the far end carries real audio. This is the PRIMARY stop
   * on this route, not a fallback: VoBiz never sends 200 OK on the A-leg — the whole
   * call, answered talking included, runs as early media — so SessionState.Established
   * never fires and cannot be used to detect pickup.
   *
   * Analyses the same stream the rep hears: the #vobiz-remote-audio element's srcObject,
   * which _attachRemoteAudio rebuilds whenever real tracks arrive. Capturing receiver
   * tracks once at dial time doesn't work — that snapshot predates the remote SDP and
   * stays silent forever, which is exactly the bug that made the ring never stop.
   *
   * Guards: 3s arming delay so the recording-start beep (~1-2s in) can't cut the ring,
   * 200ms sustain so a stray packet can't. Post-answer the callee's mic sends continuous
   * ambient audio, so this trips within ~a quarter second of pickup even if they say
   * nothing.
   */
  _armRingbackStop() {
    const rb = this._ringback;
    if (!rb) return;
    const buf = new Uint8Array(512);
    const armedAt = Date.now();
    let hotMs = 0;
    let analysedStream = null;
    let analyser = null;
    rb.vadTimer = setInterval(() => {
      if (!this._ringback) return;
      try {
        // Re-bind whenever the element's stream is replaced by a fresh track event.
        const el = document.getElementById("vobiz-remote-audio");
        const stream = el && el.srcObject;
        if (stream && stream !== analysedStream && stream.getAudioTracks().length) {
          analyser = rb.ctx.createAnalyser();
          analyser.fftSize = 512;
          rb.ctx.createMediaStreamSource(stream).connect(analyser);
          analysedStream = stream;
        }
        if (!analyser) return;
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = Math.abs(buf[i] - 128);
          if (v > peak) peak = v;
        }
        if (Date.now() - armedAt < 3000) return; // recording-beep window — ignore
        hotMs = peak > 6 ? hotMs + 100 : 0;
        if (hotMs >= 200) this._stopLocalRingback();
      } catch (_) {
        // Keep polling — a transient DOM/WebAudio hiccup must not kill the stop logic.
      }
    }, 100);
  }

  /**
   * Ring-phase poll of the server's real B-leg state (fed by VoBiz's Dial callbackUrl
   * events). This is signalling truth, not audio guesswork: stops the ring the moment
   * the callee answers OR the B-leg dies (busy / switched off / rejected) — the case
   * audio detection can never catch, because a dead B-leg just sounds like more silence.
   * Runs only while the ring is active (~30s max), every 1.5s, then stops itself.
   */
  _pollCallStateForRingStop() {
    const rb = this._ringback;
    if (!rb || !this.callSid) return;
    let inFlight = false;
    rb.pollTimer = setInterval(async () => {
      if (!this._ringback) return;
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await makeApi({ url: `${URLS.voBizCallState}/${this.callSid}`, method: "GET" });
        const state = res?.data;
        if (!state?.known) return;
        if (state.answered) {
          this._stopLocalRingback();
        } else if (state.callResult && state.callResult !== "" && state.callResult !== "ANSWERED") {
          // B-leg is dead — busy / unreachable / rejected. Stop pretending it's ringing.
          console.log("[VoBiz] B-leg ended during ring:", state.callResult);
          this._stopLocalRingback();
        }
      } catch (_) {
        // Poll failures are harmless — VAD and call-end still stop the ring.
      } finally {
        inFlight = false;
      }
    }, 1500);
  }

  _setAudioEnabled(enabled) {
    try {
      const pc = this.inviter?.sessionDescriptionHandler?.peerConnection;
      if (pc) {
        pc.getSenders().forEach(sender => {
          if (sender.track && sender.track.kind === "audio") {
            sender.track.enabled = enabled;
          }
        });
      }
    } catch (e) {
      console.error("[VoBiz] mute/unmute error:", e);
    }
  }

  _emit(event, ...args) {
    (this._listeners[event] || []).forEach(h => {
      try { h(...args); } catch (e) { console.error("[VoBiz] event handler error:", e); }
    });
  }
}

// ---------------------------------------------------------------------------
// VoBizDevice — wraps SIP.js UserAgent to look like a Twilio Device object
// ---------------------------------------------------------------------------
class VoBizDevice {
  constructor() {
    this.state = "unregistered"; // registered / registering / unregistered / destroyed
    this._isVoBiz = true;        // distinguishes VoBizDevice from Twilio Device in guards
    this._userAgent = null;
    this._registerer = null;
    this._sipDomain = null;
    this._listeners = {};
    this._activeSession = null;
    // Stub audio property so DialerSliderCard's device.audio.on() call doesn't crash
    this.audio = { on: () => {} };
  }

  /**
   * Initialises SIP.js and registers with VoBiz.
   * @param {string} sipUsername  SIP endpoint credential username (for auth)
   * @param {string} sipPassword  SIP endpoint credential password
   * @param {string} sipDomain    e.g. "registrar.vobiz.ai"
   * @param {string} wsServer     e.g. "wss://registrar.vobiz.ai:5063"
   * @param {string} fromNumber   The purchased India DID, e.g. "+918046733512".
   *                              Used as the SIP AOR so VoBiz maps outbound CLI correctly.
   *                              VoBiz ignores P-Preferred-Identity; the AOR is authoritative.
   */
  async init(sipUsername, sipPassword, sipDomain, wsServer, fromNumber) {
    this._sipDomain = sipDomain;
    this.state = "registering";
    const resolvedWsServer = wsServer || `wss://${sipDomain}`;

    // Use the SIP endpoint username as the AOR — VoBiz looks up the endpoint by AOR
    // during REGISTER and returns 401 if it doesn't match.
    // The DID (fromNumber) is used only as P-Preferred-Identity in outbound calls (connect()).
    const aorUser = sipUsername;
    console.log(`[VoBiz] init: sipUsername=${sipUsername} aorUser=${aorUser} sipDomain=${sipDomain} wsServer=${resolvedWsServer}`);

    try {
      const sipUri = `sip:${aorUser}@${sipDomain}`;
      console.log(`[VoBiz] init: making URI from ${sipUri}`);
      const uri = UserAgent.makeURI(sipUri);
      if (!uri) throw new Error("Invalid SIP URI: " + sipUri);

      this._userAgent = new UserAgent({
        uri,                              // AOR = DID number (918046733512@registrar.vobiz.ai)
        transportOptions: {
          server: resolvedWsServer
        },
        authorizationUsername: sipUsername, // endpoint credentials used for Digest auth
        authorizationPassword: sipPassword,
        logLevel: "error", // was debug — the flood buried our own [VoBiz] diagnostics
        sessionDescriptionHandlerFactoryOptions: {
          constraints: { audio: true, video: false },
          peerConnectionConfiguration: {
            iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
          }
        }
      });

      console.log(`[VoBiz] init: starting UserAgent (connecting to ${resolvedWsServer})...`);
      await this._userAgent.start();
      console.log(`[VoBiz] init: UserAgent started successfully`);

      this._registerer = new Registerer(this._userAgent);
      this._registerer.stateChange.addListener(regState => {
        console.log(`[VoBiz] Registerer state changed: ${regState}`);
        if (regState === RegistererState.Registered) {
          console.log(`[VoBiz] ✅ SIP Registration SUCCESSFUL as ${sipUsername}@${sipDomain}`);
          this.state = "registered";
          this._emit("registered");
        } else if (regState === RegistererState.Unregistered) {
          console.warn(`[VoBiz] ⚠️ SIP Unregistered`);
          this.state = "unregistered";
          this._emit("unregistered");
        }
      });

      console.log(`[VoBiz] init: sending REGISTER...`);
      await this._registerer.register();
      console.log(`[VoBiz] init: REGISTER sent`);
    } catch (e) {
      console.error(`[VoBiz] init: FAILED`, e);
      this.state = "unregistered";
      this._emit("error", e);
      throw e;
    }
  }

  /**
   * Dials toNumber via VoBiz SIP.
   * Accepts the same params object as Twilio device.connect().
   */
  async connect(arg) {
    // Twilio SDK wraps params as { params: {...} } — unwrap if needed
    const params = (arg && arg.params) ? arg.params : arg;
    const { to_number, caller_id, memberId, randomNo, task_id, contactId } = params;

    // Strip all non-digit chars from destination and caller-ID to produce clean E.164 user-parts.
    // VoBiz registered AOR uses digits-only (e.g. "918046733512"), so the P-Preferred-Identity
    // must match that format — keeping the "+" sign causes a mismatch and results in 486 Busy.
    const cleanNumber = String(to_number).replace(/[^\d]/g, "");
    // Keep leading "+" for destination if present (PSTN routing understands both; strip + for AOR match)
    const toNumberForSip = cleanNumber; // digits-only: 919443858039
    const callerIdDigits = caller_id ? String(caller_id).replace(/[^\d]/g, "") : ""; // 918046733512

    console.log("[VoBiz] connect: to_number=", to_number, "→ SIP user=", toNumberForSip, "caller_id=", caller_id, "→ AOR user=", callerIdDigits);

    const target = UserAgent.makeURI(`sip:${toNumberForSip}@${this._sipDomain}`);
    if (!target) throw new Error(`[VoBiz] Invalid target number: ${toNumberForSip}`);

    const inviter = new Inviter(this._userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false }
      },
      extraHeaders: [
        // P-Preferred-Identity must match the registered AOR (digits-only, no "+").
        // VoBiz maps this to the purchased DID for outbound CLI.
        `P-Preferred-Identity: <sip:${callerIdDigits}@${this._sipDomain}>`,
        `X-Caller-Id: ${caller_id || ""}`,
        `X-Member-Id: ${memberId || ""}`,
        `X-Random-No: ${randomNo || ""}`,
        `X-Task-Id: ${task_id || ""}`,
        `X-Contact-Id: ${contactId || ""}`
      ]
    });

    const session = new VoBizCallSession(inviter);
    session.callSid = randomNo || null;
    this._activeSession = session;

    await inviter.invite();
    return session;
  }

  disconnectAll() {
    if (this._activeSession) {
      this._activeSession.disconnect();
      this._activeSession = null;
    }
  }

  destroy() {
    try {
      this.disconnectAll();
      if (this._registerer) this._registerer.unregister();
      if (this._userAgent) this._userAgent.stop();
    } catch (e) {
      console.error("[VoBiz] destroy error:", e);
    }
    this.state = "destroyed";
  }

  // Stubs for token-related events Twilio emits but VoBiz doesn't need
  updateToken() {}

  on(event, handler) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(handler);
    return this;
  }

  once(event, handler) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      handler(...args);
    };
    return this.on(event, wrapper);
  }

  off(event, handler) {
    if (this._listeners[event]) {
      this._listeners[event] = this._listeners[event].filter(h => h !== handler);
    }
    return this;
  }

  /**
   * Re-register with VoBiz SIP — called by ensureDeviceReady() when device
   * is in "unregistered" state before a call attempt.
   */
  async register() {
    if (!this._registerer) return;
    try {
      this.state = "registering";
      await this._registerer.register();
    } catch (e) {
      this.state = "unregistered";
      console.error("[VoBiz] register() error:", e);
      throw e;
    }
  }

  _emit(event, ...args) {
    (this._listeners[event] || []).forEach(h => {
      try { h(...args); } catch (e) { console.error("[VoBiz] device event error:", e); }
    });
  }
}

export default VoBizDevice;
export { VoBizDevice };
